import DesignTokens from '@tableau/design-tokens';
import { SiteUrls } from '@tableau/shared-urls';
import { CenteredFadeInSpinner } from '@tableau/vizportalclient-common-components';
import { ButtonType, ButtonWidget, CheckBoxWithLabelWidget, TabStyles, TextFieldWidget,TextLinkWidget } from '@tableau/widgets';
import { DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { SortableDataGrid } from '~src/commonInfra/components/dataGrid/SortableDataGrid3';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { FlowListItemInfo } from '~src/flow/models/FlowInfo';
import { Flows } from '~src/flow/resources/Flows';
import { FlowWithRunPermission } from '~src/flow/serverApi/requests/GetFlowsWithRunPermissionsRequest';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';
import { ContentLineageRelationshipInfo, LineageRelationshipType, RelatedContentType } from '~src/linkedTasks/models/ContentLineageRelationshipInfo';
import { GetContentLineageRelationshipsResult } from '~src/linkedTasks/serverApi/requests/GetContentLineageRelationshipsRequest';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';
import { classes, cssClass } from '~src/style';
import { applyColor }from '~src/style/ColorService';

import { LinkedTasks } from '../resources/LinkedTasks';
import { LinkedTaskFlowState } from '../state/LinkedTaskStateTypes';
import { LinkedTaskFlowInitialState } from '../utils/LinkedTaskHelpers';
import { LinkedTaskDialogStateContext } from './LinkedTaskDialogStateContextProvider';
import { LinkedTaskItemSelectorGrid } from './LinkedTaskItemSelectorGrid';

const rootClass: string = 'LinkedTaskItemSelectorContent';

const GridWrapper = cssClass(rootClass, 'GridWrapper', {
  height: '260px',
  display: 'flex',
  flexDirection: 'column',
  $nest: {
    '& div[role=rowgroup] > div[role=row]': {
      $nest: {
        '&:hover': {
          backgroundColor: applyColor(DesignTokens.color.component.background.neutral.default.hover, '#ebebeb'),
        }
      }
    }
  }
});

const Footer = cssClass(rootClass, 'Footer', {
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '8px 16px 8px 0',
  borderTop: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.subtle, TabStyles!.Colors!.L6)}`,
});

const Header = cssClass(rootClass, 'Header', {
  display: 'flex',
  justifyContent: 'flex-start',
  padding: '8px 16px 8px 6px',
  borderBottom: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.subtle, TabStyles!.Colors!.L6)}`,
});

const GridEmptyChild = cssClass(rootClass, 'GridEmptyChild', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  height: '100%',
});

const GridEmptyText = cssClass(rootClass, 'GridEmptyText', {
  fontSize: TabStyles.Typography.Sizing.XLarge,
  color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Typography.Color.PrimaryMedium),
  textAlign: 'center',
});

const ReturnLink = cssClass(rootClass, 'ReturnLink', {
  backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.subtle, '#F5F5F5'),
  padding: '4px 8px',
  display: 'flex',
  alignItems: 'center',
});

const SearchWrapper = cssClass(rootClass, 'SearchWrapper', {
  padding: 8,
});

const backArrow = 'back_arrow.svg';

function createColumns(
  isSelected: (id: string) => boolean,
  onItemSelectionToggled: (row: DataGrid.Row) => void,
  siteUrls: SiteUrls,
): DataGrid.Column[] {
  return LinkedTaskItemSelectorGrid.contentLineageRelationshipColumn(isSelected, onItemSelectionToggled, siteUrls);
}

function createAllFlowColumn(
  isSelected: (id: string) => boolean,
  onItemSelectionToggled: (row: DataGrid.Row) => void,
  siteUrls: SiteUrls,
): DataGrid.Column[] {
  return LinkedTaskItemSelectorGrid.allFlowsColumn(isSelected, onItemSelectionToggled, siteUrls);
}

export type LinkedTaskFlowSelectorAction =
  { type: 'setFlow', payload: { index: number, flowId: string, flowName: string }};

export type LinkedTaskItemSelectorContentProps = {
  index: number;
  id: string; // the id to filter on
  selectedItemId?: string; // id of item that is already selected
  contentType: RelatedContentType;
  lineageRelationshipType: LineageRelationshipType;
  siteUrls: SiteUrls;
  closeDialog: () => void;
}

export function LinkedTaskItemSelectorContent(props: LinkedTaskItemSelectorContentProps): JSX.Element {
  const [state, dispatch] = React.useContext(LinkedTaskDialogStateContext);
  const [contentItems, setContentItems] = React.useState<ContentLineageRelationshipInfo[]>([]);
  const [selectedItems, setSelectedItems] = React.useState<{readonly [key: string]: boolean}>({});
  const [contentItemsFetched, setContentItemsFetched] = React.useState<boolean>(false);
  const [numEnabledContentItems, setNumEnabledContentItems] = React.useState<number>(0);
  const [errors, setErrors] = React.useState<IError[]>([]);

  const [viewAllFlows, setViewAllFlows] = React.useState<boolean>(false);
  const [allFlowsContentItems, setAllFlowsContentItems] = React.useState<FlowWithRunPermission[]>([]);
  const [filterText, setFilterText] = React.useState('');

  const getAllFlows = React.useCallback(() => {
    SliceableHelper.fetchAll(Flows.allFlowsWithPermission()).then(flows => {
      setAllFlowsContentItems(flows.items);
    });
  }, []);

  const onViewAllFlows = React.useCallback(() => {
    setSelectedItems({});
    setViewAllFlows(true);
    if (allFlowsContentItems.length === 0) {
      getAllFlows();
    }
  }, [getAllFlows, allFlowsContentItems.length]);

  React.useEffect(() => {
    LinkedTasks.getContentLineageRelationships(props.id, props.contentType, props.lineageRelationshipType)
      .then((result) => {
        const castResult = result as GetContentLineageRelationshipsResult;
        if (castResult.errors) {
          setErrors(castResult.errors);
          onViewAllFlows();
        } else {
          const resultItems = result as ContentLineageRelationshipInfo[];
          setContentItems(resultItems);
          if (resultItems.length === 1) {
            // If there is only one item (itself) in the results
            onViewAllFlows();
          } else {
            const enabledItems = resultItems.filter((item) => isItemEnabled(item)).length;
            setNumEnabledContentItems(enabledItems);
          }
        }
        setContentItemsFetched(true);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id, props.contentType, props.lineageRelationshipType, props.selectedItemId]);

  const memoizedGetRowData = React.useMemo(
    () => {
      return (_orderClause: IOrderClause[]) =>
        SliceableHelper.fromArray<ContentLineageRelationshipInfo>(contentItems);
    },
    [contentItems]
  );

  const memoizedGetRowDataAllFlows = React.useMemo(
    () => {
      const filteredFlows = allFlowsContentItems.filter((item: FlowWithRunPermission) => {
        return item.name.toLowerCase().indexOf(filterText.toLowerCase()) >= 0;
      });
      return (_orderClause: IOrderClause[]) =>
        SliceableHelper.fromArray<FlowWithRunPermission>(filteredFlows);
    },
    [allFlowsContentItems, filterText]
  );

  const memoizedColumns = React.useMemo(
    () => createColumns(isSelected, onItemSelectionToggled, props.siteUrls),
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedItems, props.siteUrls]
  );

  const memoizedAllFlowColumn = React.useMemo(
    () => createAllFlowColumn(isSelected, onAllFlowItemSelectionToggled, props.siteUrls),
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedItems, props.siteUrls]
  );

  function isItemEnabled(itemInfo: ContentLineageRelationshipInfo) {
    return itemInfo && itemInfo.userHasRunPermissions;
  }

  function onItemSelectionToggled(row: DataGrid.Row) {
    const data = row.data as ContentLineageRelationshipInfo;
    setSelectedItems({
      ...selectedItems,
      [data.content.id]: !selectedItems[data.content.id]
    });
  }

  function onAllFlowItemSelectionToggled(row: DataGrid.Row) {
    const data = row.data as FlowListItemInfo;
    setSelectedItems({
      ...selectedItems,
      [data.id]: !selectedItems[data.id]
    });
  }

  const selectedItemCount = Object.keys(selectedItems).filter((key) => {
    return selectedItems[key];
  }).length;


  function getAllItemsSelected() {
    const items:  {[key: string]: boolean} = {};
    contentItems.forEach(item => {
      if (isItemEnabled(item)) {
        items[item.content.id] = true;
      }
    });
    return items;
  }

  function toggleSelectAll() {
    if (selectedItemCount === numEnabledContentItems) {
      setSelectedItems({});
    } else {
      setSelectedItems(getAllItemsSelected());
    }
  }

  function isSelected(id: string): boolean {
    return !!selectedItems[id];
  }

  function getItemNameFromId(id: string) {
    if (viewAllFlows) {
      // eslint-disable-next-line eqeqeq
      const item = allFlowsContentItems.find(item => item.id == id);
      return item ? item.name : '';
    } else {
      const item = contentItems.find(item => item.content.id === id);
      return item ? item.content.name : '';
    }
  }

  function onAdd() {
    // If only one item is selected, we replace the previous flow
    if (selectedItemCount === 1) {
      const flowId = Object.keys(selectedItems).find(key => selectedItems[key]);
      dispatch({ type: 'setFlow', payload: {
        index: props.index,
        flowId: flowId,
        flowName: getItemNameFromId(flowId)
      }});
    } else {
      // If multiple items are selected, we check if the already selected item is included, and then add all the flows, in list order
      const flows: LinkedTaskFlowState[] = [];
      if (selectedItems[props.selectedItemId]) {
        flows.push(state.items[props.index] as LinkedTaskFlowState);
      }
      Object.keys(selectedItems).forEach(key => {
        if (selectedItems[key]) {
          if (key !== props.selectedItemId) {
            flows.push(LinkedTaskFlowInitialState(key, getItemNameFromId(key)));
          }
        }
      });
      dispatch({ type: 'addFlows', payload: {
        position: props.index,
        value: flows,
        overwrite: true,
      }});
    }
    props.closeDialog();
  }

  function onReturnFromViewAllFlows() {
    if (props.selectedItemId && contentItems.find((item) => item.content.id === props.selectedItemId)) {
      setSelectedItems({[props.selectedItemId]: true});
    }
    setViewAllFlows(false);
  }

  function renderEmptyChild(): JSX.Element {
    if (!contentItemsFetched) {
      return (
        <div className={GridEmptyChild}>
          <CenteredFadeInSpinner testId={'LinkedTaskItemSelectorContentSpinnerTestId'}/>
        </div>
      );
    } else if (contentItemsFetched && contentItems.length === 0) {
      const message = errors.length > 0 ? Messages.linkedTaskItemSelectorErrorMessage() :
        props.lineageRelationshipType === 'Downstream' ?
          Messages.linkedTasksNoDownstreamFlowsFound() :
          Messages.linkedTasksNoUpstreamFlowsFound();
      return <div className={classes(GridEmptyChild, GridEmptyText)} data-tb-test-id={'LinkedTaskItemSelectorEmptyTextTestId'}>{message}</div>;
    } else {
      return null;
    }
  }

  function renderEmptyAllFlows(): JSX.Element {
    if (allFlowsContentItems.length === 0) {
      return (
        <div className={GridEmptyChild}>
          <CenteredFadeInSpinner testId={'LinkedTaskItemSelectorContentSpinnerTestId'}/>
        </div>
      );
    } else {
      return null;
    }
  }

  function renderReturnLink(): JSX.Element {
    return (
      <div className={ReturnLink}>
        <img src={imgPath(backArrow)} height={20} alt={Messages.back()}/>
        <TextLinkWidget testId={'LinkedTaskItemSelectorBackToLink'} onClick={onReturnFromViewAllFlows}>
          {props.lineageRelationshipType === 'Downstream' ? Messages.linkedTasksBackToDownstream() : Messages.linkedTasksBackToUpstream()}
        </TextLinkWidget>
      </div>
    );
  }

  function onSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    setFilterText(e.target.value);
  }

  function clearSearch() {
    setFilterText('');
  }

  function renderSearch(): JSX.Element {
    return (
      <TextFieldWidget
        kind='search'
        label=''
        onChange={onSearchChange}
        onClear={clearSearch}
        placeholder={Messages.search()}
        style={{ width: '100%' }}
        testId='LinkedTaskItemSelectorSearchField'
        value={filterText}
        className={SearchWrapper}
      />
    );
  }

  function renderHeader(): JSX.Element {
    return viewAllFlows ? (
      <>
        {errors.length > 0 || contentItems.length > 1 ? renderReturnLink() : null}
        {renderSearch()}
      </>
    ) : (
      <div className={Header}>
        { contentItems.length > 0 &&
          <CheckBoxWithLabelWidget
            checked={selectedItemCount > 0 && selectedItemCount === numEnabledContentItems}
            testId={'LinkedTaskItemSelectorContentSelectAll'}
            label={Messages.selectAll()}
            handleChange={toggleSelectAll}
            disabled={!contentItemsFetched}
          />
        }
        <ButtonWidget
          buttonType={ButtonType.LowEmphasis}
          onClick={onViewAllFlows}
          testId={'LinkedTaskItemSelectorViewAllFlows'}
          style={{ marginLeft: 'auto' }}
        >
          {Messages.linkedTasksViewAllFlows()}
        </ButtonWidget>
      </div>
    );
  }

  function renderLinkedTaskSelectorDatagrid(): JSX.Element {
    const dataGridProps: Partial<DataGrid.Props> = {
      showHeaders: false,
      config: {
        rowOuterHeight: 60,
        rememberLastScrollPos: true,
      }
    };
    return (
      <SortableDataGrid
        columns={memoizedColumns}
        getSliceable={memoizedGetRowData}
        dataGridProps={dataGridProps}
        emptyChild={renderEmptyChild()}
      />
    );
  }

  function renderViewAllFlowsSelectorDatagrid(): JSX.Element {
    const dataGridProps: Partial<DataGrid.Props> = {
      showHeaders: false,
      config: {
        rowOuterHeight: 40,
        rememberLastScrollPos: true,
      },
    };
    return (
      <SortableDataGrid
        columns={memoizedAllFlowColumn}
        getSliceable={memoizedGetRowDataAllFlows}
        dataGridProps={dataGridProps}
        emptyChild={renderEmptyAllFlows()}
      />
    );
  }

  function renderFooter(): JSX.Element {
    return (
      <div className={Footer}>
        <ButtonWidget testId={'LinkedTaskItemSelectorAddButtonTestId'}
          buttonType={ButtonType.Primary}
          disabled={selectedItemCount === 0}
          handleClick={onAdd}
        >
          {selectedItemCount === 0 ?
            Messages.add() :
            Messages.addCount({ COUNT: selectedItemCount , COUNT_LOC: L10n.localizedCount(selectedItemCount) })}
        </ButtonWidget>
      </div>
    );
  }

  return (
    <div className={GridWrapper}>
      {renderHeader()}
      {viewAllFlows ? renderViewAllFlowsSelectorDatagrid() : renderLinkedTaskSelectorDatagrid()}
      {renderFooter()}
    </div>
  );
}
